import React, { useState } from 'react';
import { ActionButton } from '@/components/ui/action-button';
import { useWidth } from '@/hooks/useWidth';
import type { PatientMissingEntry, PatientHandledEntry } from '../hooks/useSessionsData';

/**
 * MissingNextSessionModal surfaces patients who had a session in the current
 * week but do not yet have a follow-up session scheduled.
 *
 * - `patients`        → pending: no form submitted yet → shows Agendar / No agendar
 * - `patientsHandled` → already managed: form was submitted → shows reason, no action buttons
 */

type MissingNextSessionModalProps = {
  patients: PatientMissingEntry[];
  patientsHandled: PatientHandledEntry[];
  onSchedule: (patientName?: string) => void;
  onNoSchedule: (patient: PatientMissingEntry) => void;
  onDismiss: () => void;
  onViewPatient?: (patient: PatientMissingEntry) => void;
};

function daysAgoLabel(dateStr: string | undefined): string | null {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  if (isNaN(then.getTime())) return null;
  const diffMs = Date.now() - then.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Hoy';
  if (days === 1) return 'Hace 1 día';
  return `Hace ${days} días`;
}

export const MissingNextSessionModal: React.FC<MissingNextSessionModalProps> = ({
  patients,
  patientsHandled,
  onSchedule,
  onNoSchedule,
  onDismiss,
  onViewPatient,
}) => {
  const total = patients.length + patientsHandled.length;
  const [handledOpen, setHandledOpen] = useState(false);
  const { size } = useWidth();
  const isMobile = size === 's';

  if (total === 0) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pacientes sin próxima sesión agendada"
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '0.75rem' : '1.5rem',
        zIndex: 120,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '1rem',
          width: 'min(560px, 100%)',
          maxHeight: 'min(80vh, 640px)',
          boxShadow: '0 20px 48px rgba(0,0,0,0.20)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: '1px solid #f0f0f0',
            padding: isMobile ? '1rem' : '1.25rem 1.5rem',
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#111827' }}>
              Pacientes sin próxima sesión
            </div>
            <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: '0.1875rem' }}>
              {patients.length === 0
                ? 'Todos los pacientes ya están gestionados'
                : patients.length === 1
                  ? '1 paciente pendiente de acción'
                  : `${patients.length} pacientes pendientes de acción`}
            </div>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onDismiss}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#9ca3af', padding: '0.125rem', flexShrink: 0, marginLeft: '1rem' }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div
          style={{
            padding: isMobile ? '0.75rem 1rem 1rem' : '1rem 1.5rem 1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            overflowY: 'auto',
            flexGrow: 1,
          }}
        >
          {/* Pending patients — need action */}
          {patients.length > 0 && (
            <div style={{ display: 'grid', gap: '0.625rem' }}>
              {patients.map((p, idx) => {
                const label = daysAgoLabel(p.lastSessionDate);
                return (
                  <div
                    key={p.patientId || `pending-${p.patientName}-${idx}`}
                    style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'stretch' : 'center',
                      justifyContent: 'space-between',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.75rem',
                      background: '#fafafa',
                      padding: '0.875rem 1rem',
                      gap: isMobile ? '0.625rem' : '0.75rem',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: '0.9375rem',
                          color: '#111827',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          ...(onViewPatient ? { cursor: 'pointer' } : {}),
                        }}
                        onMouseEnter={onViewPatient ? (e) => { (e.currentTarget as HTMLElement).style.color = '#6D57BC'; } : undefined}
                        onMouseLeave={onViewPatient ? (e) => { (e.currentTarget as HTMLElement).style.color = '#111827'; } : undefined}
                        onClick={onViewPatient ? () => onViewPatient(p) : undefined}
                      >
                        {p.patientName}
                      </div>
                      {label && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.125rem' }}>
                          Última sesión: {label}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <ActionButton
                        variant="ghost"
                        size="sm"
                        onClick={() => onNoSchedule(p)}
                        style={isMobile ? { flex: 1 } : undefined}
                        className="whitespace-nowrap border border-gray-300 transition-colors duration-300 hover:text-red-500 hover:border-red-500"
                      >
                        No agendar
                      </ActionButton>
                      <ActionButton
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          onSchedule(p.processId ?? p.patientName);
                          onDismiss();
                        }}
                        style={isMobile ? { flex: 1 } : undefined}
                        className="whitespace-nowrap"
                      >
                        Agendar
                      </ActionButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Handled patients — collapsible dropdown */}
          {patientsHandled.length > 0 && (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: '0.75rem', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setHandledOpen((o) => !o)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 1rem',
                  background: '#f9fafb',
                  border: 0,
                  cursor: 'pointer',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Ya gestionados - Formulario completado ({patientsHandled.length})
                </span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  style={{
                    flexShrink: 0,
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: handledOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <path d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {handledOpen && (
                <div style={{ display: 'grid', gap: '0.5rem', padding: '0.625rem 0.75rem 0.75rem' }}>
                  {patientsHandled.map((p, idx) => {
                    const label = daysAgoLabel(p.lastSessionDate);
                    return (
                      <div
                        key={p.patientId || `handled-${p.patientName}-${idx}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border: '1px solid #f3f4f6',
                          borderRadius: '0.625rem',
                          background: '#fff',
                          padding: '0.625rem 0.875rem',
                          gap: '0.75rem',
                          opacity: 0.85,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.patientName}
                          </div>
                          {label && (
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.125rem' }}>
                              Última sesión: {label}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissingNextSessionModal;
